import { Grid, Typography, Box } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { ThemePalette } from 'mui.theme';
import { mapVehiclesAssets } from 'utils/helpers/mapAssetImage';
import { SVG } from 'components/Asset/SVG';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import { convertSeconds } from '../../utils';
import { useAppSelector } from 'store/hook';
import { type Asset } from 'types/asset.types';
import { IgnitionStatusIndicator } from 'components/StatusIndicator';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  unitSystem: string;
  device: any;
}

const AssetItemDetails = ({ enableDarkTheme, unitSystem, device }: Props) => {
  const { t } = useTranslation();
  const { latestTagData } = useAppSelector((state) => state.deviceReducer);
  const { assets } = useAppSelector((state) => state.assetReducer);
  const assetsWithLiveData = addLiveDataToAssets(assets, latestTagData);
  const {
    data: {
      loaders: { deviceSummary },
    },
  } = useAppSelector((state) => state.productionReducer);

  const data = deviceSummary
    ?.filter(
      (item) => item.loader === device.loader && item.device_loads_per_day !== 0
    )
    ?.map((item) => {
      const assetDetail = assets.find(
        (itemData) => itemData.device.deviceId === item.loader
      );
      return {
        ...item,
        production: Number(item.total_load_for_selected_range),
        loadPerDay: Number(item.device_loads_per_day),
        haulerLoadTime: Number(item.device_hauler_load_time),
        loadPerHour: Number(item.device_loads_per_hr),
        loaderCycleTime: Number(item.device_loader_cycle_time),
        bumper_id: assetDetail?.bumperNumber,
        make: assetDetail?.make,
        model: assetDetail?.model,
        asset_type: assetDetail?.assetType,
        device: assetDetail?.assetId,
      };
    });

  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <Box
      sx={{
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <SVG
        name={mapVehiclesAssets(data[0]?.asset_type as string)}
        style={{
          fill: '#E2C171',
          height: '30px',
          width: '60px',
          paddingRight: '10px',
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '13px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'left',
                }}
              >
                <IgnitionStatusIndicator
                  latestTagData={latestTagData}
                  asset={
                    assetsWithLiveData.find(
                      (asset) => asset.bumperNumber === data[0]?.bumper_id
                    ) as Asset
                  }
                  size={10}
                  marginRight="5px"
                />
                {data[0]?.bumper_id}
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* <PowerSettingsNewIcon
                sx={{
                  color: data[0].device === 'aa' ? '#00C853' : '#E63946',
                  marginRight: '8px',
                  fontSize: '12px',
                }}
              /> */}
              <Typography
                sx={{
                  fontSize: '10px',
                  color: palette.textPrimary,
                }}
              >
                {data[0]?.make} - {data[0]?.model}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
              }}
            >
              {t('production')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
              }}
            >
              {convertToTwoDecimalPlaces(data[0]?.production?.toString())}{' '}
              {t('tons_unit')}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
              }}
            >
              {t('loads_day')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
              }}
            >
              {convertToTwoDecimalPlaces(data[0]?.loadPerDay?.toString())}{' '}
              {t('tons_unit')}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
              }}
            >
              {t('hauler_load_time')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
              }}
            >
              {convertToTwoDecimalPlaces(
                convertSeconds(data[0]?.haulerLoadTime).value.toString()
              )}{' '}
              {convertSeconds(data[0]?.haulerLoadTime).unit}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography
              sx={{
                color: palette.textSecondary,
                fontSize: '11px',
              }}
            >
              {t('loader_cycle_time')}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
              }}
            >
              {convertToTwoDecimalPlaces(
                convertSeconds(data[0]?.loaderCycleTime).value.toString()
              )}{' '}
              {convertSeconds(data[0]?.loaderCycleTime).unit}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AssetItemDetails;
